// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import CartSaveOrderButton from "@insite/content-library/Widgets/Cart/CartSaveOrderButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import MapIcon from "@insite/mobius/Icons/MapIcon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import VerifyShippingDetailsModal from "@tps/App/Components/Cart/VerifyShippingDetailsModal";
import Notify from "@tps/App/Components/Notify";
import { xGoogleAnalyticsState } from "@tps/App/GoogleAnalyticsConnector/Store/Reducers";
import xLoadingOverlay from "@tps/App/Handlers/XNG/LoadingOverlay";
import { checkoutSharedStyles } from "@tps/App/SharedStyles/CheckoutSharedStyles";
import { CustomAppState } from "@tps/App/Store/Reducers";
import CartTotalDisplay from "@tps/Overrides/Components/CartTotalDisplay";
import { Carriers } from "@tps/XDL/constants";
import IconCheck from "@tps/XDL/Icons/IconCheck";
import { StyledBox } from "@tps/XDL/Utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: CustomAppState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        // XNG-Change
        poNumberFromState: state?.xCheckout?.poNumber?.trim() || "",
        allShippingInfo: state.xShippingDetails.warehousesShippingDetails,
        hasProductWithInvalidQty: state.xCart.hasProductWithInvalidQty,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        xGoogleAnalytics: (state as unknown as xGoogleAnalyticsState).xGoogleAnalytics,
        session: state.context.session,
        dealerDetailsUrl: getPageLinkByPageType(state, "DealerDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    // XNG-Changes
    orderSummaryWrapper?: InjectableCss;
    poNumberWrapper?: InjectableCss;
    poNumberText?: TextFieldPresentationProps;
    divider?: InjectableCss;
    headingText?: TypographyPresentationProps;
    shippingVerificationSection?: InjectableCss;
    checkIcon?: IconPresentationProps;
    editLink?: LinkPresentationProps;
    branchName?: InjectableCss;
    locationLink?: LinkPresentationProps;
    locationIcon?: IconPresentationProps;
    shippingAddressMessageText?: TypographyPresentationProps;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: fixed;
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            margin-top: 10px;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitRequisitionButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    // XNG-Change
    poNumberFromState,
    allShippingInfo,
    hasProductWithInvalidQty,
    isPreloadingData,
    shipToState,
    billToState,
    xGoogleAnalytics,
    session,
    dealerDetailsUrl,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    // XNG-Change
    const [poNumber, setPONumber] = useState(poNumberFromState || cart?.poNumber || "");
    const [poNumberError, setPONumberError] = useState("");
    const poNumberRef = useRef<HTMLInputElement>(null);
    const [warehouses, setWarehouses] = useState<{
        [warehouseId: string]: { description: string; isVerified: boolean; dealerId?: string };
    }>({});
    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
    const [cartHasInvalidInfo, setCartHasInvalidInfo] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState("");
    const [checkoutDisabledMessage, setCheckoutDisabledMessage] = useState("");

    const dispatch = useDispatch();

    // <XNG-Changes>
    const isAllShippingWillCall = useMemo(() => {
        const cartWarehouses = JSON.parse(cart?.properties?.warehouses || "{}");
        return Object.keys(cartWarehouses).every(
            warehouseId =>
                cartWarehouses[warehouseId].ShipVia?.toLowerCase() === Carriers.willCall &&
                cartWarehouses[warehouseId].IsVerified,
        );
    }, [cart?.properties?.warehouses]);
    // </XNG-Changes>

    // TPS-528
    useEffect(() => {
        setPONumber(poNumberFromState || cart?.poNumber || "");
        if (!poNumberFromState && cart?.poNumber) {
            dispatch({ type: "xCheckout/SetPoNumber", poNumber: cart?.poNumber });
        }
    }, [cart?.poNumber]);

    // <XNG-Changes> update fields
    useEffect(() => {
        checkIfCartHasInvalidInfo();
    }, [cart?.cartLines, cart?.properties, poNumber, warehouses, hasProductWithInvalidQty, allShippingInfo]);

    useEffect(() => {
        let newShippingVerifiedObject: {
            [warehouseId: string]: { description: string; isVerified: boolean; dealerId?: string };
        } = {};
        if (!Object.keys(warehouses).length) {
            let warehousesObj: SafeDictionary<{ Description: string; IsVerified: boolean; DealerId?: string }> = {};
            if (cart?.properties?.warehouses) {
                warehousesObj = JSON.parse(cart?.properties?.warehouses);
            }

            const keys = Object.keys(warehousesObj);
            newShippingVerifiedObject = {};
            keys.forEach(key => {
                newShippingVerifiedObject[key] = {
                    description: warehousesObj[key]?.Description || "",
                    isVerified: warehousesObj[key]?.IsVerified || false,
                    dealerId: warehousesObj[key]?.DealerId,
                };
            });
            setWarehouses(newShippingVerifiedObject);
        } else {
            newShippingVerifiedObject = warehouses;
        }

        for (const warehouseId in allShippingInfo) {
            // if the key exists, then its verified (first time on cart we don't have shippingDetails info)
            newShippingVerifiedObject[warehouseId] = {
                description: newShippingVerifiedObject[warehouseId]?.description,
                isVerified: true,
                dealerId: newShippingVerifiedObject[warehouseId]?.dealerId,
            };
        }
        setWarehouses(newShippingVerifiedObject);
    }, [allShippingInfo]);

    useEffect(() => {
        let warehousesObj: SafeDictionary<{ Description: string; IsVerified: boolean; DealerId?: string }> = {};

        if (cart?.properties?.warehouses) {
            warehousesObj = JSON.parse(cart?.properties?.warehouses);
        }

        const keys = Object.keys(warehousesObj);
        const newShippingVerifiedObject: {
            [warehouseId: string]: { description: string; isVerified: boolean; dealerId?: string };
        } = {};
        keys.forEach(key => {
            newShippingVerifiedObject[key] = {
                description: warehousesObj[key]?.Description || "",
                isVerified: warehousesObj[key]?.IsVerified || false,
                dealerId: warehousesObj[key]?.DealerId,
            };
        });
        setWarehouses(newShippingVerifiedObject);
    }, [cart?.properties]); // when we delete a line with not verified shipping info.. the allShippingInfo is not updated but cart.propertie is

    // </XNG-Changes>
    const checkoutHandler = () => {
        if (!cart) {
            return;
        }
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        // XNG-Changes | TPS-947 : begin_checkout fire when ‘continue to checkout’ button is clicked
        if (xGoogleAnalytics.checkoutFlowEvents && cart) {
            (window as any)._GaCheckoutFunnel(
                1,
                cart,
                session.currency?.currencyCode,
                session.billToId,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
                // TPS Customization
                session,
                xGoogleAnalytics.customerNumber,
            );
        }
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        // XNG-Change we have addresses step in cart, so always bypass this step
        xLoadingOverlay({ key: "add", id: "continue_toCheckout" });
        history.push(checkoutReviewAndSubmitPageUrl!);
    };

    // XNG-Change
    const checkIfCartHasInvalidInfo = () => {
        const showWarning = !billToState?.value?.address1 || !shipToState?.value?.address1;
        if (showWarning) {
            setCartHasInvalidInfo(true);
            setCheckoutDisabledMessage(`${siteMessage("ReviewAndPay_EnterBillingShippingInfo")}`);
            return;
        }
        if (cart?.showPoNumber && cart?.requiresPoNumber && !isNonEmptyString(poNumber)) {
            setCartHasInvalidInfo(true);
            setCheckoutDisabledMessage(`${siteMessage("Custom_PO_is_required")}`);
            return;
        }
        if (hasProductWithInvalidQty) {
            setCartHasInvalidInfo(true);
            setCheckoutDisabledMessage(`${siteMessage("Custom_Invalid_Product_qty")}`);
            return;
        }
        if (Object.keys(warehouses).find(key => warehouses[key].isVerified !== true)) {
            setCartHasInvalidInfo(true);
            setCheckoutDisabledMessage(`${siteMessage("Custom_Shipping_Not_Verified")}`);
            return;
        }
        setCheckoutDisabledMessage("");
        setCartHasInvalidInfo(false);
    };

    const verifyShippingChangeHandler = (warehouseId: string) => {
        setSelectedWarehouse(warehouseId);
        setIsShippingModalOpen(true);
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for Quote");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

    const validatePONumber = (poNumber: string) => {
        const poNumberValid = !cart || !cart.showPoNumber || !cart.requiresPoNumber || isNonEmptyString(poNumber);
        setPONumberError(!poNumberValid ? translate("PO Number is required.") : "");
        return poNumberValid;
    };

    const handlePONumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPONumber(event.currentTarget.value);
        dispatch({ type: "xCheckout/SetPoNumber", poNumber: event.currentTarget.value });
        validatePONumber(event.currentTarget.value);
    };

    return (
        <LoadingOverlay loading={isLoading && !isPreloadingData}>
            <StyledBox {...styles.orderSummaryWrapper}>
                <CartTotalDisplay
                    showTaxAndShipping={showTaxAndShipping}
                    extendedStyles={styles.cartTotal}
                    isLoading={isLoading}
                    isCartEmpty={isCartEmpty}
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                    // XNG-Changes
                    roundingMessage={`${siteMessage("Custom_Rounding_Warning")}`}
                />
                <StyledWrapper {...styles.divider} />
                {cart && cart?.cartLines && cart?.cartLines?.length > 0 && (
                    <>
                        {cart.showPoNumber && (
                            <>
                                <StyledWrapper {...styles.poNumberWrapper}>
                                    <TextField
                                        {...styles.poNumberText}
                                        label={
                                            <>
                                                <span aria-hidden>
                                                    <b>{translate("PO #")}</b>
                                                    {cart.requiresPoNumber ? translate(" (Required)") : ""}
                                                </span>
                                                <VisuallyHidden>{translate("Purchase Order Number")}</VisuallyHidden>
                                            </>
                                        }
                                        value={poNumber}
                                        onChange={handlePONumberChange}
                                        required={cart.requiresPoNumber}
                                        maxLength={50}
                                        error={poNumberError}
                                        {...{ messageLabel: `${translate("PO Number")}` }}
                                        data-test-selector="checkoutReviewAndSubmit_poNumber"
                                        ref={poNumberRef}
                                        placeholder={translate("Your PO #")}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.divider} />
                            </>
                        )}
                        {/* <XNG-Changes> */}
                        {warehouses && Object.keys(warehouses).length > 0 && (
                            <>
                                <StyledWrapper {...styles.shippingVerificationSection}>
                                    <Typography {...styles.headingText}>
                                        {translate("Shipping Detail Verification")}
                                    </Typography>
                                    <StyledWrapper>
                                        {Object.keys(warehouses).map(key => {
                                            const isVerified = warehouses[key].isVerified;
                                            return (
                                                <StyledWrapper {...styles.branchName} key={key}>
                                                    {isVerified && (
                                                        <Icon {...styles.checkIcon} size={13} src={IconCheck} />
                                                    )}
                                                    {`${translate("Branch:")} ${warehouses[key]?.description}`}
                                                    {isVerified && (
                                                        <Link
                                                            {...styles.editLink}
                                                            onClick={() => verifyShippingChangeHandler(key)}
                                                        >
                                                            {translate("Edit")}
                                                        </Link>
                                                    )}
                                                    {!!warehouses[key]?.dealerId && (
                                                        <Link
                                                            {...styles.locationLink}
                                                            onClick={() =>
                                                                history.push(
                                                                    `${dealerDetailsUrl!}?id=${
                                                                        warehouses[key].dealerId
                                                                    }`,
                                                                )
                                                            }
                                                        >
                                                            <Icon {...styles.locationIcon} size={18} src={MapIcon} />
                                                        </Link>
                                                    )}
                                                    {isShippingModalOpen && selectedWarehouse === key && (
                                                        <VerifyShippingDetailsModal
                                                            isOpen={isShippingModalOpen}
                                                            setSelectedWarehouse={setSelectedWarehouse}
                                                            setIsOpen={setIsShippingModalOpen}
                                                            warehouse={{
                                                                id: key,
                                                                description: warehouses[key].description,
                                                            }}
                                                        />
                                                    )}
                                                </StyledWrapper>
                                            );
                                        })}
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.divider} />
                            </>
                        )}
                        <StyledWrapper>
                            {isAllShippingWillCall ? (
                                <>
                                    <Typography {...styles.headingText} as="p">
                                        {translate("Shipping Address")}
                                    </Typography>
                                    <Typography {...styles.shippingAddressMessageText}>
                                        {siteMessage("Custom_Will_Call_Address")}
                                    </Typography>
                                </>
                            ) : (
                                createWidgetElement("CheckoutShipping/CheckoutShippingAddresses", { fields: [] })
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.divider} />
                        {/* </XNG-Changes> */}
                    </>
                )}
                {/* XNG-Change: change button position */}
                {fields.includeActions !== false && (
                    <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} />
                )}

                {fields.includeActions !== false ? (
                    <>
                        <Button
                            {...styles.checkoutButton}
                            onClick={checkoutHandler}
                            disabled={
                                !canCheckoutWithCart ||
                                isCartCheckoutDisabled ||
                                hasOnlyQuoteRequiredProducts ||
                                cartHasInvalidInfo
                            }
                            data-test-selector="cartTotal_checkout"
                            aria-label={
                                cart?.requiresApproval
                                    ? translate("Checkout for Approval")
                                    : translate("Continue to Checkout")
                            }
                        >
                            {/* XNG-Change change label  */}
                            {cart?.requiresApproval
                                ? translate("Checkout for Approval")
                                : translate("Continue to Checkout")}
                        </Button>
                        {isPunchOutOrder && (
                            <Button
                                {...styles.cancelPunchOutButton}
                                disabled={isPunchoutButtonDisabled}
                                onClick={cancelPunchOutHandler}
                                aria-label="Cancel PunchOut"
                            >
                                {translate("Cancel PunchOut")}
                            </Button>
                        )}
                        {cart?.cartLines && cart?.cartLines.length > 0 && cartHasInvalidInfo && (
                            <Notify {...checkoutSharedStyles.notifyMsg} type="danger">
                                {checkoutDisabledMessage}
                            </Notify>
                        )}
                        <TwoButtonModal
                            headlineText=""
                            modalIsOpen={quoteRequiredModalIsOpen}
                            messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                            cancelButtonText={translate("Cancel")}
                            submitButtonText={translate("Continue")}
                            onCancel={() => setQuoteRequiredModalIsOpen(false)}
                            onSubmit={checkoutHandler}
                            submitTestSelector="QuoteInCartMessageSubmit"
                            dataTestSelector="twoButtonModal_RequiresQuote"
                        ></TwoButtonModal>
                    </>
                ) : (
                    <>
                        {cart?.properties.hardHoldMessage && (
                            <Notify {...checkoutSharedStyles.notifyMsg} type="danger">
                                {cart?.properties.hardHoldMessage}
                            </Notify>
                        )}
                        {cart?.properties.erpSystemIsDownMessage && (
                            <Notify {...checkoutSharedStyles.notifyMsg} type="danger">
                                {cart?.properties.erpSystemIsDownMessage}
                            </Notify>
                        )}
                    </>
                )}
                {/* <XNG-Changes> TPS-1057 Remove Quote Related Actions */}
                {/* {fields.includeActions !== false && canSubmitForQuote && (
                    <Button
                        {...styles.submitQuoteButton}
                        disabled={isSubmitForQuoteButtonDisabled}
                        onClick={submitForQuoteClickHandler}
                        data-test-selector="cartTotal_submitQuote"
                        aria-label="Submit Quote"
                    >
                        {submitForQuoteLabel}
                    </Button>
                )} */}
                {/* </XNG-Changes> */}
                {/* TPS-542 Disable Submit Requisition button regardless of the setting */}
                {fields.includeActions !== false && canSubmitRequisition && (
                    <>
                        <Notify {...checkoutSharedStyles.notifyMsg} type="warning">
                            {siteMessage("Custom_Requisitions_disabled_Message")}
                        </Notify>
                        <Button
                            {...styles.submitRequisitionButton}
                            disabled={true}
                            onClick={submitRequisitionClickHandler}
                            data-test-selector="cartTotal_submitRequisition"
                            aria-label="Submit Requisition"
                        >
                            {translate("Submit Requisition")}
                        </Button>
                    </>
                )}
            </StyledBox>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
